/**
 * Selectores PUROS de gente de la academia (F3-D) — sin dependencia de BD, 100%
 * testables con vitest. Cubren: particionado del directorio por pestaña,
 * filtrado por nombre/cédula/sede, extracción defensiva de estadísticas de
 * asistencia (columnas de F3-A que pueden no existir aún), render de campos
 * personalizados desde el country pack, y selección de pupilo (representante).
 */

import type { CountryPack } from "../country/packs";
import type { DirectoryMemberRow, DirectoryRow, OrgIdentity } from "./queries";

/**
 * Une las filas de membresía/expediente con el mapa de identidad (D3, plan 06).
 *
 * El directorio dejó de traer nombre/correo/imagen en su propia consulta: la
 * identidad tiene UNA fuente (`useOrgIdentities`) y se pega aquí, en un
 * selector puro. Un miembro sin identidad todavía cargada aparece con los tres
 * campos en `null` — la fila de negocio se pinta igual y la identidad hidrata
 * después, en vez del nombre en blanco permanente de antes.
 *
 * `email: null` puede significar «aún no ha cargado» o «no te corresponde
 * verlo»: para la interfaz son el mismo caso (no hay correo que enseñar), y la
 * distinción no debe vivir en el cliente.
 */
export function hydrateDirectory(
  rows: readonly DirectoryMemberRow[],
  identities: ReadonlyMap<string, OrgIdentity>,
): DirectoryRow[] {
  return attachIdentity(rows, identities, (row) => row.userId);
}

/** Los tres campos de identidad que se pegan a una fila de negocio. */
export interface IdentityFields {
  name: string | null;
  email: string | null;
  image: string | null;
}

/** Una fila de negocio ya hidratada con identidad. */
export type WithIdentity<T> = T & IdentityFields;

/**
 * Pega la identidad a CUALQUIER fila de negocio que lleve un `userId` (D3,
 * plan 06 · E3). Es la única implementación de la hidratación: el directorio,
 * el roster de cohorte, el informe de asistencia y las alertas la comparten en
 * vez de repetir cuatro `map` idénticos —que es lo que eran los cuatro
 * `leftJoin(user, …)` que esta etapa borra, solo que en SQL.
 *
 * `userId` se pasa como accesor porque las filas de PowerSync usan `user_id` y
 * las del directorio `userId`; la forma de la fila no debe obligar a duplicar
 * la lógica.
 */
export function attachIdentity<T>(
  rows: readonly T[],
  identities: ReadonlyMap<string, OrgIdentity>,
  userIdOf: (row: T) => string | null | undefined,
): WithIdentity<T>[] {
  return rows.map((row) => {
    const userId = userIdOf(row);
    const identity = userId ? identities.get(userId) : undefined;
    return {
      ...row,
      name: identity?.name ?? null,
      email: identity?.email ?? null,
      image: identity?.image ?? null,
    };
  });
}

/**
 * Ordena por nombre las filas ya hidratadas. Sustituye al `ORDER BY user.name`
 * que hacía el join borrado: sin identidad en la consulta, el orden por nombre
 * ya no puede decidirse en SQL. Las filas cuya identidad aún no ha llegado van
 * al final (con `user_id` de desempate) en vez de encabezar la lista.
 */
export function sortByIdentityName<T extends IdentityFields>(
  rows: readonly T[],
  fallbackOf: (row: T) => string,
): T[] {
  return rows.toSorted((a, b) => {
    const an = a.name ?? "";
    const bn = b.name ?? "";
    if (an && bn) {
      return an.localeCompare(bn);
    }
    if (an !== bn) {
      // Una sola con nombre: la que lo tiene va primero.
      return an ? -1 : 1;
    }
    return fallbackOf(a).localeCompare(fallbackOf(b));
  });
}

/** Las cuatro pestañas del directorio de gente. */
export type PeopleTab = "students" | "teachers" | "staff" | "guardians";

/** Mapea un `member.role` a la pestaña del directorio donde aparece. */
export function tabForRole(role: string | null | undefined): PeopleTab {
  switch (role) {
    case "teacher": {
      return "teachers";
    }
    case "owner":
    case "org_admin":
    case "admin":
    case "campus_admin":
    case "staff": {
      return "staff";
    }
    case "guardian": {
      return "guardians";
    }
    // student + legados (member/viewer/desconocido) → alumnos.
    default: {
      return "students";
    }
  }
}

/** Directorio particionado por pestaña. */
export interface PartitionedDirectory {
  students: DirectoryRow[];
  teachers: DirectoryRow[];
  staff: DirectoryRow[];
  guardians: DirectoryRow[];
}

/** Particiona filas del directorio por pestaña según el rol de la membresía. */
export function partitionDirectory(rows: readonly DirectoryRow[]): PartitionedDirectory {
  const out: PartitionedDirectory = { students: [], teachers: [], staff: [], guardians: [] };
  for (const row of rows) {
    out[tabForRole(row.role)].push(row);
  }
  return out;
}

/** Opciones de filtrado del directorio. */
export interface DirectoryFilter {
  /** Texto libre: casa con nombre, email o cédula (case-insensitive). */
  search?: string;
  /** Sede: casa contra la sede de la membresía o del expediente. */
  campusId?: string | null;
}

/** ¿La fila casa con el texto de búsqueda (nombre/email/cédula)? */
function matchesSearch(row: DirectoryRow, needle: string): boolean {
  const hay = [row.name, row.email, row.nationalId]
    .filter((v): v is string => typeof v === "string")
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

/** ¿La fila pertenece a la sede `campusId`? */
function matchesCampus(row: DirectoryRow, campusId: string): boolean {
  return row.studentCampusId === campusId || row.memberCampusId === campusId;
}

/** Aplica búsqueda + filtro de sede a un conjunto de filas. */
export function filterDirectory(
  rows: readonly DirectoryRow[],
  filter: DirectoryFilter,
): DirectoryRow[] {
  const needle = filter.search?.trim().toLowerCase() ?? "";
  const campusId = filter.campusId ?? null;
  return rows.filter((row) => {
    if (needle && !matchesSearch(row, needle)) {
      return false;
    }
    if (campusId && !matchesCampus(row, campusId)) {
      return false;
    }
    return true;
  });
}

/** Particiona y filtra en un paso (lo que consume la pantalla). */
export function buildDirectory(
  rows: readonly DirectoryRow[],
  filter: DirectoryFilter = {},
): PartitionedDirectory {
  return partitionDirectory(filterDirectory(rows, filter));
}

// ─── Asistencia (columnas de F3-A, defensivo) ────────────────────────────────

/** Estadística de asistencia derivada de `cohort_enrollment` (F3-A). */
export interface AttendanceStats {
  present: number | null;
  absent: number | null;
  late: number | null;
  excused: number | null;
  /**
   * Porcentaje 0–100 si el server lo derivó, o null. La columna
   * `attendance_rate` es una fracción 0..1 y se escala aquí (ver
   * {@link attendanceStatsOf}).
   */
  rate: number | null;
  /** ¿Hay al menos un dato de asistencia disponible? */
  hasData: boolean;
}

/** Lee un número de un objeto probando varias claves candidatas; null si no hay. */
function readNum(row: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "number" && Number.isFinite(v)) {
      return v;
    }
  }
  return null;
}

/**
 * Extrae estadísticas de asistencia de una fila cruda de `cohort_enrollment`.
 * F3-A añade las columnas más tarde; hasta entonces devuelve `hasData=false`.
 * Tolera varias convenciones de nombre para no acoplarse al esquema exacto.
 *
 * `rate` sale SIEMPRE en 0–100 porque las tres pantallas que lo consumen lo
 * pintan con un `%` detrás. La columna real que escribe el servidor
 * (`cohort_enrollment.attendance_rate`, `use_cases::attendance_stats`) es una
 * FRACCIÓN 0..1 —la misma que consumen el gradebook y el criterio de
 * certificación—, así que aquí se escala: sin ese paso una asistencia del 85 %
 * se mostraba como «0.85 %». Los alias `attendance_percent`/`attendance_pct`
 * ya vienen en porcentaje y pasan tal cual.
 */
export function attendanceStatsOf(
  raw: Record<string, unknown> | null | undefined,
): AttendanceStats {
  const row = raw ?? {};
  const present = readNum(row, ["attendance_present", "attendance_present_count", "present_count"]);
  const absent = readNum(row, ["attendance_absent", "attendance_absent_count", "absent_count"]);
  const late = readNum(row, ["attendance_late", "attendance_late_count", "late_count"]);
  const excused = readNum(row, ["attendance_excused", "attendance_excused_count", "excused_count"]);
  const fraction = readNum(row, ["attendance_rate"]);
  const percent = readNum(row, ["attendance_percent", "attendance_pct"]);
  const rate = fraction === null ? percent : Math.round(fraction * 1000) / 10;
  const hasData = [present, absent, late, excused, rate].some((v) => v !== null);
  return { present, absent, late, excused, rate, hasData };
}

// ─── Campos personalizados (country pack) ────────────────────────────────────

/** Un campo de la ficha listo para pintar (etiqueta + valor). */
export interface RenderedField {
  key: string;
  label: string;
  value: string;
}

/** Parsea `student_profile.custom_fields` (JSON) a un objeto plano seguro. */
export function parseCustomFields(
  raw: string | Record<string, unknown> | null | undefined,
): Record<string, string> {
  if (!raw) {
    return {};
  }
  // E4 (plan 06): la MISMA columna llega en dos formas según de dónde venga la
  // fila, y las dos son legítimas. Del espejo local (mi propio expediente, vía
  // `my_membership_profiles`) llega como TEXTO, porque SQLite no tiene `jsonb`;
  // del REST de expedientes llega ya como objeto. Se normaliza aquí, una vez,
  // en lugar de repetir el `JSON.parse` defensivo en cada pantalla.
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(parsed)) {
    if (v == null) {
      continue;
    }
    out[k] = typeof v === "string" ? v : String(v);
  }
  return out;
}

/**
 * Construye las filas de la FICHA de identidad usando el country pack: la cédula
 * lleva la etiqueta local (`idLabel`), seguida de los `custom_fields` propios de
 * la organización. Solo incluye campos con valor.
 */
export function renderIdentityFields(
  pack: CountryPack,
  input: {
    nationalId?: string | null;
    customFields?: string | Record<string, unknown> | null;
  },
): RenderedField[] {
  const out: RenderedField[] = [];
  if (input.nationalId) {
    out.push({ key: "national_id", label: pack.idLabel, value: input.nationalId });
  }
  for (const [key, value] of Object.entries(parseCustomFields(input.customFields))) {
    if (value) {
      out.push({ key, label: key, value });
    }
  }
  return out;
}

// ─── Representante: selección de pupilo (defensivo) ──────────────────────────

/** Un vínculo de representante mínimo (subconjunto de `guardian_link`). */
export interface WardLink {
  student_user_id: string | null;
  relationship?: string | null;
  is_primary?: number | null;
}

/**
 * Elige el pupilo activo de un representante de forma DEFENSIVA:
 *   1. el `selectedId` si sigue existiendo entre los vínculos,
 *   2. si no, el vínculo marcado `is_primary`,
 *   3. si no, el primero,
 *   4. `undefined` si no hay pupilos.
 * (Los datos del pupilo llegan cuando aterricen los streams de F4; hasta
 * entonces esto solo resuelve el ID a mostrar.)
 */
export function selectWard(
  wards: readonly WardLink[],
  selectedId?: string | null,
): string | undefined {
  const ids = wards
    .map((w) => w.student_user_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  if (ids.length === 0) {
    return undefined;
  }
  if (selectedId && ids.includes(selectedId)) {
    return selectedId;
  }
  const primary = wards.find((w) => w.is_primary === 1 && w.student_user_id);
  return primary?.student_user_id ?? ids[0];
}
